var _prefix = window._prefix;
var Dispatcher = window.Dispatcher;
var Dispatcher, _prefix;
_prefix = 'ID_';

/*
 * Dispatcher is used to broadcast payloads to registered callbacks. This is
 * different from generic pub-sub systems in two ways:
 *
 *   1) Callbacks are not subscribed to particular events. Every payload is
 *      dispatched to every registered callback.
 *   2) Callbacks can be deferred in whole or part until other callbacks have
 *      been executed.
 *
 * Example: https://facebook.github.io/flux/docs/dispatcher.html
 *
 * Usage:  Flux.Dispatcher.dispatch({actionType: a, newValue: v})
 *         Flux.Dispatcher.register((payload) -> doStuff(payload))
 */

Dispatcher = function () {
  function Dispatcher() {
    this._lastID = 1;
    this._callbacks = {};
    this._isPending = {};
    this._isHandled = {};
    this._isDispatching = false;
    this._pendingPayload = null;
  }
  Dispatcher.prototype.assert = function (condition, error) {
    if (!condition) {
      throw new Error(error);
    }
  };

  /*
   * Registers a callback to be invoked with every dispatched payload. Returns
   * a token that can be used with `waitFor()`.
   *
   * @param {function} callback
   * @return {string}
   */

  Dispatcher.prototype.register = function (callback) {
    var id;
    id = _prefix + this._lastID++;
    this._callbacks[id] = callback;
    return id;
  };

  /*
   * Removes a callback based on its token.
   *
   * @param {string} id
   */

  Dispatcher.prototype.unregister = function (id) {
    this.assert(this._callbacks[id], "Dispatcher.unregister(...): " + id + " does not map to a registered callback.");
    return delete this._callbacks[id];
  };

  /*
   * Waits for the callbacks specified to be invoked before continuing execution
   * of the current callback. This method should only be used by a callback in
   * response to a dispatched payload.
   *
   * @param {array<string>} ids
   */

  Dispatcher.prototype.waitFor = function (ids) {
    var i, id, ii, ref, results;
    this.assert(this._isDispatching, 'Dispatcher.waitFor(...): Must be invoked while dispatching.');
    results = [];
    for (ii = i = 0, ref = ids.length; 0 <= ref ? i < ref : i > ref; ii = 0 <= ref ? ++i : --i) {
      id = ids[ii];
      if (this._isPending[id]) {
        this.assert(this._isHandled[id], "Dispatcher.waitFor(...): Circular dependency detected while waiting for " + id + ".");
        continue;
      }
      this.assert(this._callbacks[id], "Dispatcher.waitFor(...): " + id + " does not map to a registered callback.");
      results.push(this._invokeCallback(id));
    }
    return results;
  };

  /*
   * Dispatches a payload to all registered callbacks.
   *
   * @param {object} payload
   */

  Dispatcher.prototype.dispatch = function (payload) {
    var id, results;
    this.assert(!this._isDispatching, "Dispatch.dispatch(...): Cannot dispatch in the middle of a dispatch.");
    this._startDispatching(payload);
    try {
      results = [];
      for (id in this._callbacks) {
        if (this._isPending[id]) {
          continue;
        }
        results.push(this._invokeCallback(id));
      }
      return results;
    } finally {
      this._stopDispatching();
    }
  };

  /*
   * Is this Dispatcher currently dispatching.
   *
   * @return {boolean}
   */

  Dispatcher.prototype.isDispatching = function () {
    return this._isDispatching;
  };

  /*
   * Call the callback stored with the given id. Also do some internal
   * bookkeeping.
   *
   * @param {string} id
   * @internal
   */

  Dispatcher.prototype._invokeCallback = function (id) {
    this._isPending[id] = true;
    this._callbacks[id](this._pendingPayload);
    return this._isHandled[id] = true;
  };

  /*
   * Set up bookkeeping needed when dispatching.
   *
   * @param {object} payload
   * @internal
   */

  Dispatcher.prototype._startDispatching = function (payload) {
    var id;
    for (id in this._callbacks) {
      this._isPending[id] = false;
      this._isHandled[id] = false;
    }
    this._pendingPayload = payload;
    return this._isDispatching = true;
  };

  /*
   * Clear bookkeeping used for dispatching.
   *
   * @internal
   */

  Dispatcher.prototype._stopDispatching = function () {
    this._pendingPayload = null;
    return this._isDispatching = false;
  };
  return Dispatcher;
}();
Flux.Dispatcher = new Dispatcher();
window.Dispatcher = Dispatcher;
window._prefix = _prefix;